import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserPreference } from '../types/mold';
import { useAuth } from '../hooks/useAuth';

export const defaultPreferences: UserPreference = {
    moldListView: 'card',
    moldOrderListView: 'card',
    kanbanBoardView: 'kanban',
    kanbanBoardType: 'maintenance',
    fontScale: 'base',
    moldDetailTab: 'tasks',
    moldPage: {
        moldOrderListView: 'table',
    },
}

interface PreferencesContextType {
    preferences: UserPreference;
    updatePreference: (data: Partial<UserPreference>) => void;
    isLoading: boolean;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [preferences, setPreferences] = useState<UserPreference>(defaultPreferences);
    const [isLoading, setIsLoading] = useState(true);

    // Load preferences when user changes
    useEffect(() => {
        if (user?.user_id) {
            const storedPreferences = localStorage.getItem(`preferences_${user.user_id}`);
            if (storedPreferences) {
                try {
                    const parsedPreferences = JSON.parse(storedPreferences);
                    setPreferences(parsedPreferences);
                } catch (error) {
                    console.error('Failed to parse stored preferences:', error);
                    setPreferences(defaultPreferences);
                }
            } else {
                setPreferences(defaultPreferences);
            }
        } else {
            // Reset to defaults when no user
            setPreferences(defaultPreferences);
        }
        setIsLoading(false);
    }, [user]);

    const updatePreference = (data: Partial<UserPreference>) => {
        if (!user?.user_id) return;

        setPreferences(prev => {
            const newPreferences = {
                ...prev,
                ...data,
                tableFilterSettings: {
                    ...prev.tableFilterSettings,
                    ...data.tableFilterSettings
                }
            };
            // Save to localStorage
            localStorage.setItem(
                `preferences_${user.user_id}`,
                JSON.stringify(newPreferences)
            );
            return newPreferences;
        });
    };

    return (
        <PreferencesContext.Provider
            value={{
                preferences,
                updatePreference,
                isLoading,
            }}
        >
            {children}
        </PreferencesContext.Provider>
    );
};

export const usePreferences = () => {
    const context = useContext(PreferencesContext);
    if (!context) {
        throw new Error('usePreferences must be used within a PreferencesProvider');
    }
    return context;
};
